import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { countPending } from '@/utils/offlineDB';
import { syncOnce } from '@/utils/offlineSync';

const OfflineSyncContext = createContext(null);

export const OfflineSyncProvider = ({ children }) => {
  const [counts, setCounts] = useState({ pending: 0, failed: 0, syncing: 0, total: 0 });
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const refreshCounts = useCallback(async () => {
    try {
      const c = await countPending();
      setCounts(c);
    } catch { /* db not ready yet */ }
  }, []);

  const triggerSync = useCallback(async () => {
    await syncOnce();
    await refreshCounts();
  }, [refreshCounts]);

  // On mount — refresh counts and attempt one sync (in case we were offline last time)
  useEffect(() => {
    refreshCounts();
    if (navigator.onLine) triggerSync();
  }, [refreshCounts, triggerSync]);

  // Auto-sync when connectivity returns
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      triggerSync();
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [triggerSync]);

  // Periodic sync when online (every 30s) and counts refresh
  useEffect(() => {
    const id = setInterval(() => {
      refreshCounts();
      if (navigator.onLine && counts.total > 0) triggerSync();
    }, 30000);
    return () => clearInterval(id);
  }, [triggerSync, refreshCounts, counts.total]);

  // React to custom events fired when an op is enqueued or completed
  useEffect(() => {
    const handler = () => refreshCounts();
    window.addEventListener('offline-queue:changed', handler);
    return () => window.removeEventListener('offline-queue:changed', handler);
  }, [refreshCounts]);

  return (
    <OfflineSyncContext.Provider value={{ counts, isOnline, triggerSync, refreshCounts }}>
      {children}
    </OfflineSyncContext.Provider>
  );
};

export const useOfflineSync = () => {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) throw new Error('useOfflineSync must be used within OfflineSyncProvider');
  return ctx;
};
